import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Search, ChevronRight, Book, Database, Workflow, AlertTriangle, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import ReactMarkdown from 'react-markdown';

const SystemDocumentation = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('');

  const sections = [
    { id: 'system-overview', title: 'System Overview', icon: Workflow },
    { id: 'module-interconnection-map', title: 'Module Interconnections', icon: Database },
    { id: 'data-flow-diagram', title: 'Data Flow', icon: ChevronRight },
    { id: 'module-details', title: 'Module Details', icon: Book },
    { id: 'database-collections', title: 'Database Collections', icon: Database },
    { id: 'field-constraints-reference', title: 'Field Constraints', icon: Settings },
    { id: 'common-issues--solutions', title: 'Common Issues', icon: AlertTriangle },
  ];

  useEffect(() => {
    fetchDocumentation();
  }, []);

  const fetchDocumentation = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/system-documentation');
      setContent(response.data.content || '');
    } catch (error) {
      toast.error('Failed to load documentation');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `WarehouseOps_Documentation_${new Date().toISOString().split('T')[0]}.md`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Documentation downloaded');
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const highlightSearch = (text) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200">$1</mark>');
  };

  const filteredContent = searchTerm
    ? content.split('\n').filter(line => 
        line.toLowerCase().includes(searchTerm.toLowerCase())
      ).join('\n')
    : content;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <FileText className="w-5 h-5" />
                System Documentation
              </h1>
              <p className="text-sm text-[#E8DCC8]">Complete module reference & workflows</p>
            </div>
          </div>
          <Button
            onClick={handleDownload}
            className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
            data-testid="download-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download MD
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="hidden lg:block w-64 bg-white border-r border-[#E5DFD0] min-h-[calc(100vh-64px)] sticky top-16 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-[#5A5F6E] uppercase tracking-wider mb-4">
              Contents
            </h3>
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                    activeSection === section.id
                      ? 'bg-[#2D5F4F] text-white'
                      : 'text-[#5A5F6E] hover:bg-[#F5F0E8]'
                  }`}
                >
                  <section.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{section.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 max-w-5xl mx-auto px-4 md:px-8 py-8">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search documentation..."
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            {searchTerm && (
              <p className="mt-2 text-sm text-[#5A5F6E]">
                Showing results for "{searchTerm}"
              </p>
            )}
          </div>

          {/* Documentation Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D5F4F]"></div>
              <span className="ml-3 text-[#5A5F6E]">Loading documentation...</span>
            </div>
          ) : (
            <article className="prose prose-slate max-w-none 
              prose-headings:text-[#1A1F2E] prose-headings:font-display
              prose-h1:text-3xl prose-h1:border-b prose-h1:border-[#E5DFD0] prose-h1:pb-4
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:pt-6 prose-h2:border-t prose-h2:border-[#E5DFD0]
              prose-h3:text-xl prose-h3:text-[#2D5F4F]
              prose-h4:text-lg prose-h4:text-[#5A5F6E]
              prose-p:text-[#5A5F6E] prose-p:leading-relaxed
              prose-a:text-[#2D5F4F] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-[#1A1F2E]
              prose-code:text-[#2D5F4F] prose-code:bg-[#F5F0E8] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
              prose-pre:bg-[#1A1F2E] prose-pre:text-[#E8DCC8] prose-pre:overflow-x-auto
              prose-table:border-collapse prose-table:w-full
              prose-th:bg-[#F5F0E8] prose-th:text-[#1A1F2E] prose-th:font-semibold prose-th:px-4 prose-th:py-2 prose-th:border prose-th:border-[#E5DFD0]
              prose-td:px-4 prose-td:py-2 prose-td:border prose-td:border-[#E5DFD0]
              prose-li:text-[#5A5F6E]
              prose-blockquote:border-l-4 prose-blockquote:border-[#2D5F4F] prose-blockquote:bg-[#F5F0E8] prose-blockquote:py-2 prose-blockquote:px-4
            ">
              <ReactMarkdown
                components={{
                  h2: ({ node, children, ...props }) => {
                    const id = children?.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                    return <h2 id={id} {...props}>{children}</h2>;
                  },
                  h3: ({ node, children, ...props }) => {
                    const id = children?.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                    return <h3 id={id} {...props}>{children}</h3>;
                  },
                  // Custom code block styling
                  pre: ({ node, children, ...props }) => (
                    <pre className="bg-[#1A1F2E] text-[#E8DCC8] p-4 rounded-lg overflow-x-auto text-sm" {...props}>
                      {children}
                    </pre>
                  ),
                  // Custom table styling
                  table: ({ node, children, ...props }) => (
                    <div className="overflow-x-auto my-4">
                      <table className="min-w-full border-collapse" {...props}>
                        {children}
                      </table>
                    </div>
                  ),
                }}
              >
                {filteredContent}
              </ReactMarkdown>
            </article>
          )}

          {/* Quick Links Footer */}
          <div className="mt-12 pt-8 border-t border-[#E5DFD0]">
            <h3 className="text-lg font-semibold text-[#1A1F2E] mb-4">Quick Links</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/dashboard')}
                className="justify-start"
              >
                <Settings className="w-4 h-4 mr-2" />
                Admin Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/wms')}
                className="justify-start"
              >
                <Database className="w-4 h-4 mr-2" />
                WMS Module
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/goods-transfer')}
                className="justify-start"
              >
                <Workflow className="w-4 h-4 mr-2" />
                Goods Transfer
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/wms-reports')}
                className="justify-start"
              >
                <FileText className="w-4 h-4 mr-2" />
                WMS Reports
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SystemDocumentation;
